import React, { useEffect, useState } from "react";
import {
  Col,
  Container,
  Row,
  Spinner,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalHeader,
  ModalBody,
  Button,
} from "reactstrap";
import classnames from "classnames";

// redux
import { shallowEqual, useDispatch, useSelector } from "react-redux";

// router
import { useLocation, useNavigate } from "react-router-dom";

import {
  fetchCompany,
  fetchCouponSummary,
  fetchPrimSummary,
  fetchServiceCommissionSummary,
} from "../../../slices/apps/companySlice";
import {
  fetchCouponsByCompany,
  fetchPrimsByCompany,
  fetchServicesByCompany,
} from "../../../slices/apps/orderSlice";

// sayfalar, modallar vb.
import Widgets from "./Widgets";
import PrimTable from "./PrimTable";
import CouponTable from "./CouponTable";
import ServiceTable from "./ServiceTable";
import UpdateModal from "./UpdateModal";
import UpdateLogoModal from "./UpdateLogoModal";

// Sayfaya özel stiller.
const ModernStyles = () => (
  <style>{`
    .profile-avatar {
      width: 80px;
      height: 80px;
      background-color: #eef2f7;
      color: var(--bs-primary);
      font-size: 2.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      font-weight: bold;
      flex-shrink: 0;
      overflow: hidden; 
      position: relative;
      border: 1px solid #dee2e6;
      transition: all 0.2s;
    }
    .profile-avatar.has-logo {
      cursor: pointer;
    }
    .profile-avatar.has-logo:hover {
      border-color: var(--bs-primary);
      box-shadow: 0 0 0 2px rgba(var(--bs-primary-rgb), 0.2);
    }

    .detail-item {
      margin-bottom: 1rem;
    }
    .detail-item label {
      font-size: 0.8rem;
      font-weight: 600;
      color: #878a99;
      margin-bottom: 0.25rem;
      display: block;
      text-transform: uppercase;
    }
    .detail-item p {
      font-weight: 500;
      color: #495057;
      margin-bottom: 0;
      word-break: break-word;
    }
    .nav-tabs-modern {
      border-bottom: 2px solid #f0f0f0;
    }
    .nav-tabs-modern .nav-link {
      border: none;
      color: #878a99;
      font-weight: 600;
      padding: 1rem;
    }
    .nav-tabs-modern .nav-link.active {
      color: var(--bs-primary);
      background-color: transparent;
      border-bottom: 2px solid var(--bs-primary);
    }
    .table thead {
      background-color: #f8f9fa;
    }
  `}</style>
);

const CompanyDetail = () => {
  document.title = "Firma Detayları | Metiner Avantaj+";

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { companyId } = location.state || {};

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("1");
  const [qrModal, setQrModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const targetUrl = `https://app.metiner.com.tr/customer/login?companyId=${companyId}`;

  const [logoPreviewModal, setLogoPreviewModal] = useState(false);

  const toggleTab = (tab) => activeTab !== tab && setActiveTab(tab);
  const toggleQrModal = () => setQrModal(!qrModal);

  const toggleLogoPreview = () => setLogoPreviewModal(!logoPreviewModal);

  const { company, companyRefresh } = useSelector((state) => {
    return {
      company: state.company.company,
      companyRefresh: state.company.companyRefresh,
    };
  }, shallowEqual);

  const fetchData = async () => {
    setLoading(true);

    try {
      await dispatch(fetchCompany(companyId)).unwrap();
      await dispatch(fetchServicesByCompany(companyId)).unwrap();
      await dispatch(fetchServiceCommissionSummary(companyId)).unwrap();
      await dispatch(fetchCouponsByCompany(companyId)).unwrap();
      await dispatch(fetchCouponSummary(companyId)).unwrap();
      await dispatch(fetchPrimsByCompany(companyId)).unwrap();
      await dispatch(fetchPrimSummary(companyId)).unwrap();
    } catch (error) {
      navigate("company");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchData();
    } else {
      navigate("/company");
    }
  }, [companyId, dispatch, companyRefresh]);

  const handleCopy = () => {
    navigator.clipboard.writeText(targetUrl);
    setCopied(true);

    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  if (loading) {
    return (
      <div
        className="page-content d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <Spinner color="primary">Yükleniyor...</Spinner>
      </div>
    );
  }

  return (
    <React.Fragment>
      <ModernStyles />
      <div className="page-content">
        <Container fluid>
          <Row className="mb-2">
            <Col xs={12}>
              <Card>
                <CardBody>
                  <div className="d-flex flex-column flex-md-row align-items-center ">
                    <div className="position-relative mb-3 mb-md-0">
                      <div
                        className={`profile-avatar ${
                          company?.companyLogoUrl ? "has-logo" : ""
                        }`}
                        onClick={() =>
                          company?.companyLogoUrl && toggleLogoPreview()
                        }
                        title={
                          company?.companyLogoUrl
                            ? "Logoyu büyütmek için tıklayın"
                            : ""
                        }
                      >
                        {company?.companyLogoUrl ? (
                          <img
                            src={company.companyLogoUrl}
                            alt="Logo"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                          />
                        ) : company?.companyName ? (
                          company.companyName.charAt(0)
                        ) : null}
                      </div>

                      {companyId && (
                        <UpdateLogoModal
                          companyId={companyId}
                          currentLogo={company?.companyLogoUrl}
                        />
                      )}
                    </div>

                    <div className="ms-md-4 text-center text-md-start flex-grow-1">
                      <h5 className="mb-1">{company?.companyName}</h5>
                      <p className="text-muted mb-0">{company?.taxOffice}</p>
                    </div>

                    <div className="ms-md-auto mt-3 mt-md-0 d-flex gap-2 align-items-center justify-content-center">
                      {company?.qrUrl && (
                        <Button
                          color="primary"
                          className="btn-sm"
                          onClick={toggleQrModal}
                        >
                          <i className="ri-qr-code-line align-bottom me-2"></i>
                          QR Kod
                        </Button>
                      )}

                      <UpdateModal id={companyId} />
                    </div>
                  </div>
                </CardBody>

                <CardBody className="border-top pt-4">
                  <Row>
                    <Col md={4}>
                      <div className="detail-item">
                        <label>Vergi Numarası</label>
                        <p>{company?.taxNumber}</p>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="detail-item">
                        <label>Telefon</label>
                        <p>{company?.phoneNumber}</p>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="detail-item">
                        <label>E-posta</label>
                        <p>{company?.email}</p>
                      </div>
                    </Col>
                    <Col md={12}>
                      <div className="detail-item mb-0">
                        <label>Adres</label>
                        <p>{company?.address}</p>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <Card>
                <CardBody>
                  <Nav tabs className="nav-tabs-modern">
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "1" })}
                        onClick={() => toggleTab("1")}
                        style={{ cursor: "pointer" }}
                      >
                        Ciro Primi
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "2" })}
                        onClick={() => toggleTab("2")}
                        style={{ cursor: "pointer" }}
                      >
                        Kupon Hak Ediş
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "3" })}
                        onClick={() => toggleTab("3")}
                        style={{ cursor: "pointer" }}
                      >
                        Kayıt Primi
                      </NavLink>
                    </NavItem>
                  </Nav>
                </CardBody>
              </Card>
              <TabContent activeTab={activeTab}>
                <TabPane tabId="1">
                  <Row className="gy-3">
                    <Col xxl="12">
                      <Widgets tabType="ciro" />
                    </Col>
                    <Col xxl="12">
                      <ServiceTable companyId={companyId} />
                    </Col>
                  </Row>
                </TabPane>
                <TabPane tabId="2">
                  <Row className="gy-3">
                    <Col xxl="12">
                      <Widgets tabType="coupon" />
                    </Col>
                    <Col xxl="12">
                      <CouponTable companyId={companyId} />
                    </Col>
                  </Row>
                </TabPane>
                <TabPane tabId="3">
                  <Row className="gy-3">
                    <Col xxl="12">
                      <Widgets tabType="prim" />
                    </Col>
                    <Col xxl="12">
                      <PrimTable companyId={companyId} />
                    </Col>
                  </Row>
                </TabPane>
              </TabContent>
            </Col>
          </Row>
        </Container>

        <Modal isOpen={qrModal} toggle={toggleQrModal} centered>
          <ModalHeader toggle={toggleQrModal}>
            {company?.companyName} - QR Kod
          </ModalHeader>
          <ModalBody className="text-center p-4">
            {company?.qrUrl && (
              <img
                src={company.qrUrl}
                alt="QR Detay"
                className="img-fluid mb-4"
                style={{ maxHeight: "350px" }}
              />
            )}

            <div className="text-start">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  value={targetUrl}
                  readOnly
                  style={{ backgroundColor: "#f8f9fa" }}
                />
                <button
                  className={`btn ${copied ? "btn-success" : "btn-primary"}`}
                  type="button"
                  onClick={handleCopy}
                >
                  {copied ? (
                    <span>
                      <i className="fa fa-check"></i> Kopyalandı
                    </span>
                  ) : (
                    <span>
                      <i className="fa fa-copy"></i> Kopyala
                    </span>
                  )}
                </button>
              </div>
            </div>
          </ModalBody>
        </Modal>

        <Modal isOpen={logoPreviewModal} toggle={toggleLogoPreview} centered>
          <ModalHeader toggle={toggleLogoPreview}>
            {company?.companyName} - Logo
          </ModalHeader>
          <ModalBody className="text-center">
            {company?.companyLogoUrl && (
              <img
                src={company.companyLogoUrl}
                alt="Firma Logosu"
                className="img-fluid"
                style={{ maxHeight: "400px" }}
              />
            )}
          </ModalBody>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default CompanyDetail;
